import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Ghost,
  Sparkles,
  Play,
  ChevronDown,
  Check,
  Zap,
  Clock,
  TrendingUp,
  Video,
  Upload,
  Link as LinkIcon,
  X,
  Loader2,
  Info,
  Copy,
} from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { Progress } from "./ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "./ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { ScrollArea } from "./ui/scroll-area";
import { toast } from "sonner";
import { ghostAPI } from "../lib/api";

// Energy level colors
const ENERGY_COLORS = {
  low: "bg-blue-500",
  medium: "bg-yellow-500",
  high: "bg-orange-500",
  explosive: "bg-red-500",
};

// Pacing icons
const PACING_ICONS = {
  slow: "🐢",
  moderate: "🚶",
  fast: "🏃",
  frenetic: "⚡",
};

export default function GhostModePanel({
  mode = "ugc", // "ugc" or "video"
  onGhostProfileSelect,
  onGhostModeToggle,
  isEnabled = false,
}) {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [customProfile, setCustomProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [showTemplates, setShowTemplates] = useState(false);
  const [activeTab, setActiveTab] = useState("templates");

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await ghostAPI.getTemplates();
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error("Failed to fetch templates:", error);
    }
  };

  const handleSelectTemplate = async (template) => {
    setLoading(true);
    try {
      const response = await ghostAPI.getTemplate(template.id);
      setSelectedTemplate(response.data);
      setCustomProfile(null);
      onGhostProfileSelect?.(response.data);
      toast.success(`Ghost template "${template.name}" loaded!`);
    } catch (error) {
      toast.error("Failed to load template");
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyzeVideo = async () => {
    if (!videoUrl.trim()) {
      toast.error("Please enter a video URL");
      return;
    }

    setAnalyzing(true);
    try {
      const response = await ghostAPI.analyzeVideo(videoUrl);
      if (response.data.success) {
        setCustomProfile(response.data.profile);
        setSelectedTemplate(null);
        onGhostProfileSelect?.(response.data.profile);
        toast.success("Video analyzed! Ghost profile created.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to analyze video");
    } finally {
      setAnalyzing(false);
    }
  };

  const activeProfile = selectedTemplate || customProfile;

  return (
    <Card className={`border-2 transition-colors ${isEnabled ? 'border-purple-500 bg-purple-500/5' : 'border-border/50 bg-card'}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isEnabled ? 'bg-purple-500' : 'bg-secondary'}`}>
              <Ghost className={`w-5 h-5 ${isEnabled ? 'text-white' : 'text-muted-foreground'}`} />
            </div>
            <div>
              <CardTitle className="text-white flex items-center gap-2">
                Ghost Mode
                <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0 text-xs">
                  NEW
                </Badge>
              </CardTitle>
              <CardDescription>
                {mode === "ugc" ? "Subject Persistence" : "Character Alignment"}
              </CardDescription>
            </div>
          </div>
          <Switch
            checked={isEnabled}
            onCheckedChange={onGhostModeToggle}
            data-testid="ghost-mode-toggle"
          />
        </div>
      </CardHeader>

      <AnimatePresence>
        {isEnabled && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <CardContent className="space-y-4">
              {/* Mode Description */}
              <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/30">
                <p className="text-sm text-purple-300">
                  {mode === "ugc" ? (
                    <>
                      <strong>Subject Persistence:</strong> Your product will remain 100% visible and legible.
                      Labels and logos will not warp during motion.
                    </>
                  ) : (
                    <>
                      <strong>Character Alignment:</strong> Your character will be mapped onto the Ghost's
                      motion path with 1:1 sync for transitions and movements.
                    </>
                  )}
                </p>
              </div>

              {/* Tab Selection */}
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-2 bg-secondary/50">
                  <TabsTrigger value="templates" className="gap-2">
                    <Sparkles className="w-4 h-4" />
                    Templates
                  </TabsTrigger>
                  <TabsTrigger value="analyze" className="gap-2">
                    <Video className="w-4 h-4" />
                    Analyze Video
                  </TabsTrigger>
                </TabsList>

                {/* Templates Tab */}
                <TabsContent value="templates" className="mt-4 space-y-3">
                  <ScrollArea className="h-[200px] pr-4">
                    <div className="space-y-2">
                      {templates.map((template) => (
                        <div
                          key={template.id}
                          onClick={() => handleSelectTemplate(template)}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            selectedTemplate?.id === template.id
                              ? 'border-purple-500 bg-purple-500/10'
                              : 'border-border/30 bg-secondary/20 hover:border-purple-500/50 hover:bg-secondary/30'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium text-white text-sm">{template.name}</h4>
                                {selectedTemplate?.id === template.id && (
                                  <Check className="w-4 h-4 text-purple-400" />
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">{template.description}</p>
                              <div className="flex items-center gap-2 mt-2">
                                <Badge variant="outline" className="text-xs">
                                  {template.duration}s
                                </Badge>
                                <Badge 
                                  variant="outline" 
                                  className={`text-xs ${
                                    template.overall_energy === 'explosive' ? 'border-red-500 text-red-400' :
                                    template.overall_energy === 'high' ? 'border-orange-500 text-orange-400' :
                                    template.overall_energy === 'medium' ? 'border-yellow-500 text-yellow-400' :
                                    'border-blue-500 text-blue-400'
                                  }`}
                                >
                                  {PACING_ICONS[template.pacing]} {template.pacing}
                                </Badge>
                                <Badge variant="outline" className="text-xs capitalize">
                                  {template.style?.replace('_', ' ')}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </TabsContent>

                {/* Analyze Video Tab */}
                <TabsContent value="analyze" className="mt-4 space-y-3">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground text-sm">Video URL</Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Paste TikTok, Instagram, or video URL..."
                        value={videoUrl}
                        onChange={(e) => setVideoUrl(e.target.value)}
                        className="bg-secondary/30 border-border/50"
                      />
                      <Button
                        onClick={handleAnalyzeVideo}
                        disabled={analyzing || !videoUrl.trim()}
                        className="bg-purple-500 hover:bg-purple-600"
                      >
                        {analyzing ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Zap className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      AI will analyze the video and extract its motion profile, timing, and energy.
                    </p>
                  </div>

                  {customProfile && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-3 rounded-lg bg-green-500/10 border border-green-500/30"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Check className="w-4 h-4 text-green-400" />
                        <span className="text-sm font-medium text-green-400">Profile Extracted!</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div>
                          <span className="text-muted-foreground">Duration:</span>
                          <span className="text-white ml-1">{customProfile.duration}s</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Energy:</span>
                          <span className="text-white ml-1 capitalize">{customProfile.overall_energy}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Pacing:</span>
                          <span className="text-white ml-1 capitalize">{customProfile.pacing}</span>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </TabsContent>
              </Tabs>

              {/* Active Profile Summary */}
              {activeProfile && (
                <div className="p-3 rounded-lg bg-secondary/30 border border-border/30">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-white">Active Ghost Profile</span>
                    <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">
                      {selectedTemplate ? 'Template' : 'Custom'}
                    </Badge>
                  </div>
                  
                  {/* Energy Curve Visualization */}
                  {activeProfile.energy_curve && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Energy Curve</Label>
                      <div className="flex items-end gap-[2px] h-8">
                        {activeProfile.energy_curve.slice(0, 30).map((energy, i) => (
                          <div
                            key={i}
                            className="flex-1 rounded-t transition-all"
                            style={{
                              height: `${energy * 100}%`,
                              backgroundColor: energy > 0.8 ? '#ef4444' : 
                                              energy > 0.6 ? '#f97316' :
                                              energy > 0.4 ? '#eab308' : '#3b82f6',
                              minHeight: '2px'
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Hook Markers */}
                  {activeProfile.hook_markers && activeProfile.hook_markers.length > 0 && (
                    <div className="mt-3 space-y-1">
                      <Label className="text-xs text-muted-foreground">Hook Timing</Label>
                      <div className="flex flex-wrap gap-1">
                        {activeProfile.hook_markers.map((hook, i) => (
                          <Badge
                            key={i}
                            variant="outline"
                            className="text-xs"
                            title={hook.description}
                          >
                            {hook.timestamp}s - {hook.hook_type.replace('_', ' ')}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Instructions Preview */}
                  {(activeProfile.camera_instructions || activeProfile.subject_instructions) && (
                    <div className="mt-3 space-y-2">
                      {activeProfile.camera_instructions && (
                        <div>
                          <Label className="text-xs text-muted-foreground">Camera Direction</Label>
                          <p className="text-xs text-white/80 mt-1">{activeProfile.camera_instructions}</p>
                        </div>
                      )}
                      {activeProfile.subject_instructions && (
                        <div>
                          <Label className="text-xs text-muted-foreground">Subject Direction</Label>
                          <p className="text-xs text-white/80 mt-1">{activeProfile.subject_instructions}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Mode-specific Info */}
              <div className="flex items-start gap-2 text-xs text-muted-foreground">
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <p>
                  {mode === "ugc" ? (
                    "Product images will use Subject Binding to maintain 100% consistency and legibility throughout the video."
                  ) : (
                    "Your character will be aligned with the Ghost's skeletal motion path for perfect sync."
                  )}
                </p>
              </div>
            </CardContent>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}
